<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import {
    NutzungsgradWaermeerzeugerHeizung,
    NutzungsgradWaermeerzeugerHeizungCollection,
} from '@/models/NutzungsgradWaermeerzeugerHeizung';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'waermeerzeuger_reihenfolge' },
    { direction: 'asc', property: 'installationsperiode_reihenfolge' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new NutzungsgradWaermeerzeugerHeizungCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: NutzungsgradWaermeerzeugerHeizung) {
    router.push({ name: 'bg_geb_nutzungsgrad_heizung_edit', params: { id: r?.id } });
}

async function deleteRecord(r: NutzungsgradWaermeerzeugerHeizung) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_geb_nutzungsgrad_heizung_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('nutzungsgrade_heizung')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as NutzungsgradWaermeerzeugerHeizung)"
            @deleterecord="deleteRecord($event as NutzungsgradWaermeerzeugerHeizung)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="waermeerzeuger_heizung" :header="$t('waermeerzeuger_heizung')"></Column>
            <Column field="installationsperiode" sortable :header="$t('installationsperiode')"></Column>
            <Column field="nutzungsgrad" sortable :header="$t('nutzungsgrad')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
